import { useReducer } from "react";
import { Badge, Collapse, Divider, List } from "@mui/material";
import {
  AdminPanelSettings,
  AssignmentInd,
  Badge as BadgeIcon,
  DesktopWindows,
  DevicesOther,
  DirectionsCar,
  GroupAdd,
  Dashboard,
  PieChart,
  Settings,
  SupervisorAccountOutlined,
  Tune
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { useInquiryCount } from "../hooks/use-inquiry-count";
import { CapacityDrawerList } from "./capacity-drawer-list.component";
import { FacilityDrawerList } from "./facility-drawer-list.component";
import { ListItemLink } from "./list-item-link.component";
import { MainDrawerList } from "./main-drawer-list.component";
import { DrawerList } from "./drawer-list.component";
import { Offset } from "../ResponsiveDrawer/responsive-drawer-offset.component";
import { ServiceDrawerList } from "./service-drawer-list.component";
import { selectVisitorManagementEnabled } from "../../../features/Login/selectors/selectVisitorManagementEnabled";
import { getBookingTypeEnabled } from "../../../features/Admin/CompanyCard/SettingDialog/company-card-booking-type-enabling.config";
import type { MetaData } from "../../../features/Login/typings/login.types";
type S = {
  administration: boolean;
  capacity: boolean;
  bookingActivities: boolean;
  service: boolean;
};
type A =
  | { type: "administration/toggle"; open: boolean }
  | { type: "capacity/toggle"; open: boolean }
  | { type: "service/toggle"; open: boolean }
  | { type: "bookingActivities/toggle"; open: boolean };

const reducer: React.Reducer<S, A> = (state, action) => {
  switch (action.type) {
    case "administration/toggle":
      return { ...state, administration: action.open };
    case "capacity/toggle":
      return { ...state, capacity: action.open };
    case "service/toggle":
      return { ...state, service: action.open };
    case "bookingActivities/toggle":
      return { ...state, bookingActivities: action.open };
    default:
      throw new Error("Invalid dispatch action");
  }
};

type P = {
  onToggleDrawerMobile: () => void;
  /** whether the responsive drawer is open or collapsed */
  isOpen: boolean;
  mobileOpen: boolean;
};

export const ResponsiveDrawerItems: React.FC<P> = ({
  onToggleDrawerMobile,
  isOpen,
  mobileOpen
}) => {
  const { t } = useTranslation();
  const {
    userInformation: {
      company: { meta: companyMeta }
    },
    userRoles: {
      isTenantAdmin,
      isCompanyAdmin,
      isEmployeeResponsible,
      isHrAdmin,
      isDeviceResponsible,
      isLocationAdmin,
      isCapacityResponsible,
      isRequestAdmin,
      isFleetAdmin,
      isVisitorAdmin
    }
  } = useSelector((state: RootState) => state.login);
  const visitorManagementEnabled = useSelector(selectVisitorManagementEnabled);
  const visitorBookingsEnabled =
    visitorManagementEnabled &&
    getBookingTypeEnabled(companyMeta as MetaData, "visitorBookingEnabled");
  const { inquiryCount } = useInquiryCount();

  const [toggled, dispatch] = useReducer(reducer, {
    administration: false,
    capacity: false,
    bookingActivities: false,
    service: false
  });

  const DISPLAY_BOOKING_ACTIVITIES = false;
  const DISPLAY_WORKPLACE_CATEGORY_REPORT = false;

  const isTenantOrCompanyAdmin = isTenantAdmin || isCompanyAdmin;
  const isDisplayAdministrationList =
    isTenantOrCompanyAdmin ||
    isHrAdmin ||
    isDeviceResponsible ||
    isLocationAdmin ||
    isCapacityResponsible ||
    isRequestAdmin ||
    isFleetAdmin ||
    isVisitorAdmin;

  const onToggleCapacityList = () => {
    dispatch({ type: "capacity/toggle", open: false });
  };
  const onToggleServiceList = () => {
    dispatch({ type: "service/toggle", open: false });
  };

  const onCloseSubLIst = () => {
    onToggleCapacityList();
    onToggleServiceList();
  };

  const subListProps = {
    disablePadding: true,
    onClick: () => {
      onToggleDrawerMobile();
      onCloseSubLIst();
    },
    sx: { paddingLeft: isOpen ? "11%" : 0 }
  };

  return (
    <>
      <Offset />

      <Divider sx={{ opacity: "0.6" }} />

      {/* Home, Booking, Schedule */}
      <MainDrawerList
        onToggleDrawerMobile={onToggleDrawerMobile}
        toggleAdmin={() => {
          dispatch({ type: "administration/toggle", open: false });
          onCloseSubLIst();
        }}
      />

      {/* Connections */}
      <List
        disablePadding
        data-testid={"drawer-connection-toggle"}
        onClick={() => {
          onToggleDrawerMobile();
          dispatch({ type: "administration/toggle", open: false });
          onCloseSubLIst();
        }}
      >
        <ListItemLink
          id="connections"
          data-testid={"drawer-connections-toggle"}
          to={"/connections"}
          primary={t("Connections")}
          icon={
            <Badge badgeContent={inquiryCount} color="primary">
              <GroupAdd />
            </Badge>
          }
        />
      </List>

      {/* Supervisor */}
      <List
        disablePadding
        data-testid={"drawer-supervisor-toggle"}
        onClick={() => {
          onToggleDrawerMobile();
          dispatch({ type: "administration/toggle", open: false });
          onCloseSubLIst();
        }}
      >
        {isEmployeeResponsible && (
          <ListItemLink
            id="supervisor"
            to={"/supervisor"}
            primary={t("Supervisor")}
            data-testid={"drawer-supervisor-toggle"}
            icon={<SupervisorAccountOutlined />}
          />
        )}
      </List>

      <Divider sx={{ margin: "5px 0", opacity: "0.6" }} />

      {/* List of items following to user roles, i.e. location admin, hr admin, etc. */}
      <List disablePadding>
        {isDisplayAdministrationList && (
          <ListItemLink
            id="administration"
            to={"/administration"}
            primary={t("Drawer_Administration")}
            data-testid="drawer-administration-toggle"
            icon={<Settings />}
            onClick={e => {
              e.preventDefault();
              dispatch({ type: "administration/toggle", open: !toggled.administration });
              onCloseSubLIst();
            }}
            isToggled={toggled.administration}
          />
        )}

        <Collapse in={toggled.administration}>
          {/* Organization - Company, Department, and Users */}
          <List {...subListProps} data-testid={"drawer-organization-toggle"}>
            {isTenantOrCompanyAdmin && (
              <ListItemLink
                id="organization"
                to={"/admin/organization"}
                primary={t("Organization")}
                data-testid={"drawer-organization-toggle"}
                icon={<Tune />}
              />
            )}
          </List>

          {/* Buildings - create/edit the location, floor, and the floor plan */}
          {/* isLocationAdmin || isCompanyAdmin || isTenantAdmin */}
          <FacilityDrawerList
            onToggleDrawerMobile={onToggleDrawerMobile}
            onCloseSubLIst={onCloseSubLIst}
            isOpen={isOpen}
          />

          {/* Inventory */}
          <List {...subListProps} data-testid={"drawer-inventory-toggle"}>
            {(isDeviceResponsible || isFleetAdmin) && (
              <ListItemLink
                id="inventory"
                to={"/equipment/inventory"}
                primary={t("Drawer_Inventory")}
                data-testid={"drawer-inventory-toggle"}
                icon={<DevicesOther />}
              />
            )}
          </List>

          {/* Fleet Management */}
          <List {...subListProps} data-testid={"drawer-fleet-toggle"}>
            {isFleetAdmin && (
              <ListItemLink
                id="fleet"
                to={"/admin/fleet"}
                primary={t("Drawer_Fleet")}
                data-testid={"drawer-fleet-toggle"}
                icon={<DirectionsCar />}
              />
            )}
          </List>

          {/* Visitor bookings */}
          <List {...subListProps} data-testid={"drawer-visitors-toggle"}>
            {visitorBookingsEnabled && (
              <ListItemLink
                id="visitors"
                to={"/admin/visitors"}
                primary={t("Drawer_Visitors")}
                data-testid={"drawer-visitors-toggle"}
                icon={<BadgeIcon />}
              />
            )}
          </List>

          {/* Management dashboard — tenant or company admin; above Capacity */}
          <List {...subListProps} data-testid={"drawer-management-toggle"}>
            {isTenantOrCompanyAdmin && (
              <ListItemLink
                id="management"
                to={"/management"}
                primary={t("Management")}
                data-testid={"drawer-management-toggle"}
                icon={<Dashboard />}
              />
            )}
          </List>

          {/* Capacity Report */}
          {/* isCapacityResponsible */}
          <CapacityDrawerList
            onToggleDrawerMobile={onToggleDrawerMobile}
            onToggleList={() => dispatch({ type: "capacity/toggle", open: true })}
            onCloseCapacityList={onToggleCapacityList}
            isToggled={toggled.capacity}
            isOpen={isOpen}
            mobileOpen={mobileOpen}
          />

          {/* Service - Booking requests, catering list, service orders */}
          {/* isRequestAdmin, activateBookingSerivce */}
          <ServiceDrawerList
            onToggleDrawerMobile={onToggleDrawerMobile}
            onToggleList={() => dispatch({ type: "service/toggle", open: true })}
            onCloseServiceList={onToggleServiceList}
            isToggled={toggled.service}
            isOpen={isOpen}
            mobileOpen={mobileOpen}
          />

          {DISPLAY_WORKPLACE_CATEGORY_REPORT && (
            <List {...subListProps} data-testid={"drawer-category-report-toggle"}>
              {isDeviceResponsible && (
                <ListItemLink
                  id="workplace-category-report"
                  to={"/equipment/capacity"}
                  primary={t("Drawer_Category Report")}
                  data-testid="drawer-category-report-toggle"
                  icon={<PieChart />}
                />
              )}
            </List>
          )}

          {/* HR */}
          <List data-testid={"drawer-hr-toggle"} {...subListProps}>
            {isHrAdmin && (
              <ListItemLink
                id={"hr-report"}
                primary={t("HR")}
                to={"/hr-report"}
                data-testid={"drawer-hr-toggle"}
                icon={<AssignmentInd />}
              />
            )}
          </List>

          {/* Administrators */}
          <List {...subListProps} data-testid={"drawer-administratiors-toggle"}>
            {isTenantOrCompanyAdmin && (
              <ListItemLink
                id={"administrators"}
                to={"/admin/admins"}
                primary={t("Administrators")}
                data-testid={"drawer-administrators-toggle"}
                icon={<AdminPanelSettings />}
              />
            )}
          </List>

          {/* Booking Activities, not used currently */}
          {DISPLAY_BOOKING_ACTIVITIES && (
            <DrawerList
              onToggleDrawerMobile={onToggleDrawerMobile}
              onToggleList={() => {
                dispatch({ type: "bookingActivities/toggle", open: false });
                onCloseSubLIst();
              }}
              isToggled={toggled.bookingActivities}
              isOpen={isOpen}
              icon={<DesktopWindows />}
              name={"booking-activities"}
              link1name={"occupation"}
              link2name={"contact-tracing"}
            />
          )}
        </Collapse>
      </List>
    </>
  );
};
